import { motion } from "framer-motion";
import { useLang } from "@/i18n";
import { XCircle, CheckCircle2, ShieldCheck } from "lucide-react";

const ROWS_BEFORE = [
  { db: "CRIF · Eurisc", stato: "Contenzioso · 3 rate insolute", esito: "Mutuo: RIFIUTATO" },
  { db: "Centrale Rischi BdI", stato: "Sofferenza attiva", esito: "Fido e carta: REVOCATI" },
  { db: "CTC", stato: "Pregiudizievole pendente", esito: "Finanziamento auto: RESPINTO" },
];

const ROWS_AFTER = [
  { db: "CRIF · Eurisc", stato: "Nessuna segnalazione presente", esito: "Mutuo: APPROVABILE" },
  { db: "Centrale Rischi BdI", stato: "Posizione eliminata", esito: "Fido e conti: OPERATIVI" },
  { db: "CTC", stato: "Archivio ripulito ex GDPR", esito: "Prestiti e rate: ACCETTATI" },
];

export default function PrimaDopo() {
  const { t } = useLang();
  return (
    <section className="py-20 lg:py-28" data-testid="prima-dopo-section">
      <div className="max-w-6xl mx-auto px-5 lg:px-8">
        <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-80px" }} transition={{ duration: 0.7 }} className="text-center max-w-2xl mx-auto">
          <p className="text-xs uppercase tracking-[0.2em] font-bold text-[#00AEEF]">{t("pd.kicker")}</p>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-[#003087] mt-3">{t("pd.title")}</h2>
          <p className="mt-4 text-base text-[#64748B]">{t("pd.sub")}</p>
        </motion.div>

        <div className="mt-14 grid lg:grid-cols-2 gap-6">
          <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true, margin: "-60px" }} transition={{ duration: 0.6 }}
            className="rounded-3xl border-2 border-red-200 bg-white p-7" data-testid="visura-before-card">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <span className="text-[11px] font-bold uppercase tracking-wider text-red-500">{t("pd.before")}</span>
              <span className="text-[11px] font-bold text-red-500">{t("pd.beforeScore")}</span>
            </div>
            <span className="mt-3 inline-flex items-center gap-1.5 bg-red-50 border border-red-200 text-red-600 text-[11px] font-bold rounded-full px-3 py-1.5">
              <XCircle size={12} /> {t("pd.beforeBadge")}
            </span>
            <div className="mt-5 space-y-3">
              {ROWS_BEFORE.map((r, i) => (
                <div key={i} className="rounded-xl bg-red-50/60 border border-red-100 px-4 py-3">
                  <div className="flex justify-between items-center gap-2">
                    <p className="text-xs font-bold text-[#1A1A1A]">{r.db}</p>
                    <p className="text-[10px] font-bold text-red-500">{r.esito}</p>
                  </div>
                  <p className="text-[11px] text-[#64748B] mt-1">{r.stato}</p>
                </div>
              ))}
            </div>
            <p className="mt-5 text-xs font-semibold text-red-600">{t("pd.sumBefore")}</p>
          </motion.div>

          <motion.div initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true, margin: "-60px" }} transition={{ duration: 0.6, delay: 0.12 }}
            className="rounded-3xl border-2 border-[#00AEEF]/40 bg-white p-7 shadow-xl" data-testid="visura-after-card">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <span className="text-[11px] font-bold uppercase tracking-wider text-[#00AEEF]">{t("pd.after")}</span>
              <span className="text-[11px] font-bold text-[#00AEEF]">{t("pd.afterScore")}</span>
            </div>
            <span className="mt-3 inline-flex items-center gap-1.5 bg-[#E6F6FD] border border-[#00AEEF]/30 text-[#0077A3] text-[11px] font-bold rounded-full px-3 py-1.5">
              <CheckCircle2 size={12} /> {t("pd.afterBadge")}
            </span>
            <div className="mt-5 space-y-3">
              {ROWS_AFTER.map((r, i) => (
                <div key={i} className="rounded-xl bg-[#E6F6FD]/70 border border-[#00AEEF]/20 px-4 py-3">
                  <div className="flex justify-between items-center gap-2">
                    <p className="text-xs font-bold text-[#1A1A1A]">{r.db}</p>
                    <p className="text-[10px] font-bold text-[#0077A3]">{r.esito}</p>
                  </div>
                  <p className="text-[11px] text-[#64748B] mt-1">{r.stato}</p>
                </div>
              ))}
            </div>
            <p className="mt-5 text-xs font-semibold text-[#0077A3]">{t("pd.sumAfter")}</p>
          </motion.div>
        </div>

        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: 0.2 }} className="mt-8 text-center">
          <span className="inline-flex items-center gap-2 bg-[#003087] text-white text-sm font-bold rounded-full px-6 py-3.5" data-testid="prima-dopo-guarantee">
            <ShieldCheck size={16} className="text-[#00AEEF]" /> {t("pd.pill")}
          </span>
          <p className="mt-3 text-[10px] text-[#94A3B8]">{t("pd.demo")}</p>
        </motion.div>
      </div>
    </section>
  );
}
